import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Paper,
  Alert,
  CircularProgress,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  Backup as BackupIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Download as DownloadIcon,
  Schedule as ScheduleIcon,
  Storage as StorageIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../utils/axios';

interface BackupResult {
  success: boolean;
  message: string;
  downloadUrl?: string;
  backupId?: string;
  timestamp?: string;
  size?: string;
}

const BackupPage: React.FC = () => {
  const { user } = useAuth();
  const [backingUp, setBackingUp] = useState(false);
  const [result, setResult] = useState<BackupResult | null>(null);
  const [recentBackups, setRecentBackups] = useState<any[]>([]);

  const handleCreateBackup = async () => {
    setBackingUp(true);
    setResult(null);

    try {
      const response = await api.post('/backup/create', {
        includeFiles: true,
        includeDatabase: true,
        description: `Manual backup created by ${user?.email}`
      });

      setResult({
        success: true,
        message: 'Backup created successfully',
        backupId: response.data.backupId,
        timestamp: response.data.timestamp,
        size: response.data.size,
        downloadUrl: response.data.downloadUrl
      });

      // Refresh recent backups list
      fetchRecentBackups();
    } catch (error: any) {
      setResult({
        success: false,
        message: error.response?.data?.message || 'Backup creation failed'
      });
    }

    setBackingUp(false);
  };

  const fetchRecentBackups = async () => {
    try {
      const response = await api.get('/backup/list');
      setRecentBackups(response.data.backups || []);
    } catch (error) {
      console.error('Failed to fetch recent backups:', error);
    }
  };

  const handleDownloadBackup = async (backupId: string) => {
    try {
      const response = await api.get(`/backup/download/${backupId}`, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `backup_${backupId}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download backup:', error);
    }
  };

  React.useEffect(() => {
    fetchRecentBackups();
  }, []);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Database Backup
      </Typography>
      
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          Create a complete backup of your database and uploaded files. 
          Backups include all data and can be used for disaster recovery or migration.
        </Typography>
      </Alert>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Create New Backup
              </Typography>
              
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  This will create a complete backup including:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <StorageIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Database (all tables and data)" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <DownloadIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Uploaded files and attachments" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <ScheduleIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Configuration and settings" />
                  </ListItem>
                </List>
              </Box>

              <Button
                variant="contained"
                onClick={handleCreateBackup}
                disabled={backingUp}
                startIcon={backingUp ? <CircularProgress size={20} /> : <BackupIcon />}
                fullWidth
                size="large"
              >
                {backingUp ? 'Creating Backup...' : 'Create Backup'}
              </Button>
            </CardContent>
          </Card>

          {result && (
            <Card sx={{ mt: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Backup Result
                </Typography>
                
                <Paper sx={{ p: 2, border: 1, borderColor: 'divider' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    {result.success ? (
                      <SuccessIcon color="success" />
                    ) : (
                      <ErrorIcon color="error" />
                    )}
                    <Typography variant="subtitle2">
                      {result.success ? 'Backup Successful' : 'Backup Failed'}
                    </Typography>
                    {result.success && result.size && (
                      <Chip 
                        label={result.size} 
                        size="small" 
                        color="success" 
                      />
                    )}
                  </Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {result.message}
                  </Typography>
                  {result.success && result.backupId && (
                    <Typography variant="body2" color="text.secondary">
                      Backup ID: {result.backupId}
                    </Typography>
                  )}
                  {result.success && result.timestamp && (
                    <Typography variant="body2" color="text.secondary">
                      Created: {new Date(result.timestamp).toLocaleString()}
                    </Typography>
                  )}
                  {result.success && result.downloadUrl && (
                    <Button
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      onClick={() => handleDownloadBackup(result.backupId!)}
                      sx={{ mt: 1 }}
                    >
                      Download Backup
                    </Button>
                  )}
                </Paper>
              </CardContent>
            </Card>
          )}
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Recent Backups
              </Typography>
              
              {recentBackups.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No recent backups found.
                </Typography>
              ) : (
                <List>
                  {recentBackups.map((backup, index) => (
                    <React.Fragment key={backup.id}>
                      <ListItem>
                        <ListItemIcon>
                          <BackupIcon />
                        </ListItemIcon>
                        <ListItemText
                          primary={`Backup ${backup.id}`}
                          secondary={
                            <Box>
                              <Typography variant="body2">
                                {new Date(backup.createdAt).toLocaleString()}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Size: {backup.size || 'Unknown'}
                              </Typography>
                            </Box>
                          }
                        />
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<DownloadIcon />}
                          onClick={() => handleDownloadBackup(backup.id)}
                        >
                          Download
                        </Button>
                      </ListItem>
                      {index < recentBackups.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default BackupPage;
